import { Html, OrbitControls } from '@react-three/drei';
import { Physics, RigidBody } from '@react-three/rapier';
import { Perf } from 'r3f-perf';
import { useLoader, useFrame } from '@react-three/fiber';
import { TextureLoader } from 'three';
import * as THREE from 'three';
import { Ball } from './Ball';
import { EffectComposer, DotScreen } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import { useState, useEffect } from 'react';

// Create a separate component for the 3D scene
function Scene({ startFalling }) {
    const loadTexture = (path) => {
        const texture = useLoader(TextureLoader, path);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(2, 1);
        return texture;
    };

    const C = loadTexture('/C.png');
    const CS = loadTexture('/CS.png');
    const express = loadTexture('/express.png');
    const java = loadTexture('/java.png');
    const js_dark = loadTexture('/js-dark.png');
    const js_light = loadTexture('/js-light.png');
    const mongodb = loadTexture('/mongodb.png');
    const node = loadTexture('/node.png');
    const python = loadTexture('/python.png');
    const react_dark = loadTexture('/react-dark.png');
    const react_light = loadTexture('/react-light.png');
    const threejs = loadTexture('/threejs.png');
    const unity = loadTexture('/unity.png');
    const vue = loadTexture('/vue.png');

    const width = window.innerWidth;
    const sphereScale = width < 1000 ? 0.4 : 1;
    const wall = width < 1000 ? 0.5 : 1;

    return (
        <>
            <EffectComposer>
                <DotScreen
                    blendFunction={BlendFunction.NORMAL}
                    angle={Math.PI * 0.5}
                    scale={2.0}
                />
            </EffectComposer>

            <directionalLight castShadow position={[1, 2, 3]} intensity={5} />
            <ambientLight intensity={1.5} />
            
            <Physics>
                {startFalling && (
                    <>
                        <Ball
                            position={[-4 * sphereScale, 22, 0]}
                            color="#1E90FF"
                            texture={C}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[0, 20, 0]}
                            color="mediumpurple"
                            texture={CS}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[-4 * sphereScale, 18, 0]}
                            color="#87CEFA"
                            texture={express}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[-2 * sphereScale, 18, 0]}
                            color="orange"
                            texture={java}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[2 * sphereScale, 18, 0]}
                            color="white"
                            texture={js_dark}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[-3 * sphereScale, 14, 0]}
                            color="#00FF7F"
                            texture={mongodb}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[0, 16, 0]}
                            color="#ff8fa3"
                            texture={node}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[3 * sphereScale, 14, 0]}
                            color="#f6aa1c"
                            texture={python}
                            scale={sphereScale}
                        />


                        <Ball
                            position={[-3 * sphereScale, 24, 0]}
                            color="#DA7B93"
                            texture={react_dark}
                            scale={sphereScale}
                        />

                        <Ball
                            position={[2 * sphereScale, 24, 0]}
                            color="gold"
                            texture={threejs}
                            scale={sphereScale}
                        />
                        
                        <Ball
                            position={[4 * sphereScale, 24, 0]}
                            color="#809aba"
                            texture={unity}
                            scale={sphereScale}
                        />
                        <Ball
                            position={[-4 * sphereScale, 12, 0]}
                            color="#52b788"
                            texture={vue}
                            scale={sphereScale}
                        />
                    </>
                )}

                {/* Ground (Invisible box bottom) */}
                <RigidBody type="fixed" restitution={1}>
                    <mesh receiveShadow position={[0, -(window.innerHeight / 200) + 1.70, 0]}>
                        <boxGeometry args={[window.innerWidth / 100, wall, 1.5]} />
                        <meshStandardMaterial transparent={true} opacity={0} />
                    </mesh>
                </RigidBody>

                {/* Left, Right, Front, and Back Walls */}
                <RigidBody type="fixed">
                    <mesh position={[-window.innerWidth / 200 - 0.20, -1.5, 0]}>
                        <boxGeometry args={[0.5, window.innerHeight / 50, 2.5 * wall]} />
                        <meshStandardMaterial transparent={true} opacity={0} />
                    </mesh>
                </RigidBody>
                <RigidBody type="fixed">
                    <mesh position={[window.innerWidth / 200 + 0.20, -1.5, 0]}>
                        <boxGeometry args={[0.5, window.innerHeight / 50, 2.5 * wall]} />
                        <meshStandardMaterial transparent={true} opacity={0} />
                    </mesh>
                </RigidBody>
                <RigidBody type="fixed">
                    <mesh position={[0, -1.5, -1.5 * wall]}>
                        <boxGeometry args={[window.innerWidth / 75, window.innerHeight / 50, 0.5]} />
                        <meshStandardMaterial transparent={true} opacity={0} />
                    </mesh>
                </RigidBody>
                <RigidBody type="fixed">
                    <mesh position={[0, -1.5, 1.5 * wall]}>
                        <boxGeometry args={[window.innerWidth / 75, window.innerHeight / 50, 0.5]} />
                        <meshStandardMaterial transparent={true} opacity={0} />
                    </mesh>
                </RigidBody>
            </Physics>
        </>
    );
}

// Main Experience component
export default function Experience() {
    const [startFalling, setStartFalling] = useState(false);

    useEffect(() => {
        const section = document.querySelector("#technologies");

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setStartFalling(true);
                    }
                });
            },
            { threshold: 0.5 }
        );

        if (section) {
            observer.observe(section);
        }

        return () => {
            if (section) {
                observer.unobserve(section);
            }
        };
    }, []);

    return (
        <>
            <Html position={[0, window.innerHeight / 175, 0]}>
                <div className="tech-container">
                    <h1 className="title">TECHNOLOGIES</h1>
                </div>
            </Html>
            <Scene startFalling={startFalling} />
        </>
    );
}
